import React from 'react';
import Parser from "html-react-parser";

const HealthInfoPolicy = () => {
  const datas = [
    {
      id: 1,
      title: '1. Purpose of Collection',
      contents: `HERINGS Inc. ("the Company") collects health and medical information to provide personalized healthcare insights, meal recommendations, and treatment-related support through the Healiary Global service ("the Service").<br/><br/>
      This policy explains how health information is collected, used, stored, and protected in accordance with applicable data protection laws, including the EU General Data Protection Regulation (GDPR) and other international health data regulations.`
    },
    {
      id: 2,
      title: '2. Types of Health Information Collected',
      contents: `The Company collects the following categories of health-related information:<br/><br/>
      <strong>a. Cancer and Treatment Information</strong><br/>
      • Cancer type and stage<br/>
      • Treatment type (chemotherapy, radiation, immunotherapy, etc.)<br/>
      • Surgery status and timing<br/>
      • Ongoing treatment schedule<br/>
      • Treatment response and side effects<br/><br/>

      <strong>b. Physical Health Data</strong><br/>
      • Height, weight, and BMI<br/>
      • Blood pressure and vital signs<br/>
      • Physical activity levels<br/>
      • Sleep patterns<br/>
      • Pain and symptom logs<br/><br/>

      <strong>c. Medical History</strong><br/>
      • Chronic conditions and comorbidities<br/>
      • Medication history and current prescriptions<br/>
      • Food allergies and dietary restrictions<br/>
      • Family medical history (optional)<br/><br/>

      <strong>d. Nutritional and Lifestyle Data</strong><br/>
      • Daily meal records and food intake<br/>
      • Nutritional intake analysis<br/>
      • Dietary preferences and restrictions<br/>
      • Exercise and activity logs<br/>
      • Mood and wellbeing tracking`
    },
    {
      id: 3,
      title: '3. How We Collect Health Information',
      contents: `Health information is collected through:<br/>
      • Direct input by users during registration and ongoing use<br/>
      • Health questionnaires and surveys<br/>
      • Meal and symptom logging features<br/>
      • Integration with wearable devices or health apps (with user consent)<br/>
      • Healthcare provider reports (with explicit consent)`
    },
    {
      id: 4,
      title: '4. Legal Basis for Processing Health Data',
      contents: `Health information is classified as "special category" or "sensitive" personal data under GDPR Article 9. The Company processes health data based on:<br/><br/>
      <strong>a. Explicit Consent (Art. 9(2)(a) GDPR)</strong><br/>
      Users provide explicit consent during account creation to allow the collection and processing of health information for service delivery.<br/><br/>

      <strong>b. Contractual Necessity (Art. 6(1)(b) GDPR)</strong><br/>
      Processing health data is necessary to fulfill the contract and provide personalized health recommendations and meal plans.<br/><br/>

      <strong>Important Notice:</strong> Because Healiary's core functionality depends on health information, providing this data is required to use the Service. If you do not consent to the collection and use of health data, you will not be able to create an account or access the Service.`
    },
    {
      id: 5,
      title: '5. How We Use Health Information',
      contents: `Health information is used exclusively for:<br/>
      • Providing personalized meal plans and nutritional recommendations<br/>
      • Tracking symptoms and treatment side effects<br/>
      • Generating health insights and progress reports<br/>
      • Sending medication and appointment reminders<br/>
      • Improving service quality through anonymized data analysis<br/>
      • Conducting medical research (only with separate consent and using anonymized data)<br/><br/>

      <strong>We do not:</strong><br/>
      • Sell or rent health information to third parties<br/>
      • Use health data for commercial advertising without consent<br/>
      • Share identifiable health data with insurance companies or employers`
    },
    {
      id: 6,
      title: '6. Data Storage and Security',
      contents: `<strong>a. Storage Location</strong><br/>
      All health data is stored on secure Amazon Web Services (AWS) servers located in Frankfurt, Germany (eu-central-1), ensuring compliance with EU data protection standards.<br/><br/>

      <strong>b. Security Measures</strong><br/>
      The Company implements industry-standard security measures, including:<br/>
      • End-to-end encryption for data in transit (TLS 1.3)<br/>
      • AES-256 encryption for data at rest<br/>
      • Multi-factor authentication and role-based access controls<br/>
      • Regular security audits and penetration testing<br/>
      • Employee training on data protection and confidentiality<br/>
      • Automated backup systems and disaster recovery plans<br/><br/>

      <strong>c. Data Retention</strong><br/>
      Health information is retained as long as your account remains active. Upon account deletion or withdrawal of consent, your health data is either:<br/>
      • Permanently deleted within 30 days, or<br/>
      • Irreversibly anonymized for statistical and research purposes (no longer considered personal data under GDPR)`
    },
    {
      id: 7,
      title: '7. Data Sharing and Disclosure',
      contents: `<strong>a. Service Providers</strong><br/>
      We may share health data with trusted third-party service providers who assist in operating the Service (e.g., cloud hosting, analytics). These providers are bound by strict data-processing agreements and may only use data for authorized purposes.<br/><br/>

      <strong>b. Healthcare Professionals</strong><br/>
      With your explicit consent, health data may be shared with your healthcare providers to support clinical decision-making or treatment planning.<br/><br/>

      <strong>c. Legal Requirements</strong><br/>
      We may disclose health information when legally required by court order, regulatory authorities, or to protect the rights and safety of users or others.<br/><br/>

      <strong>d. Research Purposes</strong><br/>
      Anonymized health data may be used for medical research or public health studies, but only with your separate consent and after ensuring data cannot be re-identified.`
    },
    {
      id: 8,
      title: '8. Your Rights',
      contents: `You have the following rights regarding your health information:<br/><br/>
      <strong>a. Right of Access</strong><br/>
      You may request a copy of all health data we hold about you.<br/><br/>

      <strong>b. Right to Rectification</strong><br/>
      You may correct inaccurate or incomplete health information at any time through the app or by contacting us.<br/><br/>

      <strong>c. Right to Erasure ("Right to be Forgotten")</strong><br/>
      You may request deletion of your health data. Upon deletion, your data will be permanently removed or anonymized within 30 days.<br/><br/>

      <strong>d. Right to Restrict Processing</strong><br/>
      You may request that we limit how your health data is used.<br/><br/>

      <strong>e. Right to Data Portability</strong><br/>
      You may request your health data in a structured, machine-readable format (e.g., CSV, JSON) to transfer to another service.<br/><br/>

      <strong>f. Right to Withdraw Consent</strong><br/>
      You may withdraw your consent to health data processing at any time. However, withdrawal will prevent continued use of the Service.<br/><br/>

      <strong>g. Right to Lodge a Complaint</strong><br/>
      You may file a complaint with your local Data Protection Authority if you believe your rights have been violated.<br/><br/>

      To exercise any of these rights, please contact us at <strong>product@heringsglobal.com</strong>. We will respond within one month.`
    },
    {
      id: 9,
      title: '9. Children and Minors',
      contents: `The Service is not intended for individuals under 16 years of age (or the applicable minimum age in your jurisdiction). We do not knowingly collect health information from children. If we discover that a child's data has been collected, it will be promptly deleted.`
    },
    {
      id: 10,
      title: '10. International Data Transfers',
      contents: `Health data is stored within the European Economic Area (EEA) on AWS servers in Germany. In limited cases, data may be transferred outside the EEA for technical support or service operations, but only with adequate safeguards such as:<br/>
      • Standard Contractual Clauses (SCCs) approved by the European Commission<br/>
      • Binding Corporate Rules (BCRs)<br/>
      • Adequacy decisions by the European Commission`
    },
    {
      id: 11,
      title: '11. Updates to This Policy',
      contents: `We may update this Health Information Policy to reflect changes in law, technology, or business practices. Any material changes will be communicated via email, in-app notification, or website announcement at least 30 days before taking effect.<br/><br/>
      Continued use of the Service after changes take effect constitutes acceptance of the revised policy.`
    },
    {
      id: 12,
      title: '12. Contact Us',
      contents: `For questions or concerns about how we handle your health information, please contact:<br/><br/>
      <strong>HERINGS Inc.</strong><br/>
      Data Protection Officer: Jennie Bae<br/>
      Email: product@heringsglobal.com<br/>
      Website: <a href='https://heringsglobal.com' class='font-bold'>https://heringsglobal.com</a><br/><br/>

      <strong>EU Representative (for GDPR inquiries):</strong><br/>
      HERINGS Inc.<br/>
      6F, 20, Gukjegeumyung-ro, Yeongdeungpo-gu, Seoul, South Korea<br/>
      Email: herings@heringsglobal.com`
    },
  ];

  return (
    <div className='text-gray2 keepAll select-text'>
      <h1 className='text-body3_700 text-gray_160 mb-4'>Healiary Global Health Information Policy</h1>
      <p className='text-caption2_400 text-gray_120 mb-4'>Version: v1.0<br/>Effective Date: 2025-10-31</p>

      <ul>
        {datas.map(data =>
          <li key={data.id} className='mb-4'>
            <h3 className='text-caption2_700 text-gray_160 mb-2'>{data.title}</h3>
            <div className='text-caption2_400 text-gray_120'>{Parser(data.contents)}</div>
          </li>
        )}
      </ul>
    </div>
  );
};

export default HealthInfoPolicy;
